import React, { useEffect, useMemo } from 'react';
import Dashboard from '../../../components/shared/dashboard/dashboard';
import { ReservationListPageStyled } from '../../customers/reservation-list/reservation-list.page.styled';
import i18n from '../../../translations/strings';
import List from '../../../components/shared/list/list';
import {
  deleteReservationAction,
  getReservationListAction,
  getReservationListResetState,
} from '../../../store/shared/reservations-list.store';
import { useDispatch, useSelector } from 'react-redux';
import {
  getDateTimeBooking, getDisplayNameStatusBooking, getRoom, getSmartObject, getStringDateName,
} from '../../../utils/utils';
import {
  ListStyled,
  sxList,
} from '../../../components/shared/list/list.styled';
import { SvgImage, SvgType } from '../../../components/base/svg/svg';
import { Colors } from '../../../colors';
import { ReservationListAdminPageStyled } from './reservation-list-admin.page.styled';
import { useNavigate } from 'react-router-dom';
import { ModalVariant } from '../../../components/shared/modal-provider/components/modal/modal.constants';
import { useModal } from '../../../components/shared/modal-provider/hooks/modal-provider.hook';
import TableCell from '@mui/material/TableCell';

const ReservationListAdminPage = () => {
  const { openModal } = useModal();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const statuses = ['CONFIRMED', 'SENT', 'CANCELED_BY_ADMIN'];

  const {
    reservationsList,
    paginationInfo,
    loading,
    error,
    rooms,
    smartObjects,
    updateDeleteSuccess,
  } = useSelector((state: any) => {
    return state.reservationsList;
  });

  const fields = useMemo(() => {
    return [
      {
        name: 'created_at',
        displayName: i18n.t('reservations.booking'),
        component: (item: any) => {
          const date = getStringDateName(item.created_at);
          return <ListStyled.CustomRow>{date}</ListStyled.CustomRow>;
        },
      },
      {
        name: 'id_reservation',
        displayName: i18n.t('reservations.reservationId'),
        component: (item: any) => {
          return <ListStyled.CustomRow>{item.reference_number ? item.reference_number : item.id}</ListStyled.CustomRow>;
        },
      },
      {
        name: 'applicant',
        displayName: i18n.t('reservations.applicant'),
        component: (item: any) => (
          <ListStyled.CustomRow>
            {item.user_information?.full_name}
          </ListStyled.CustomRow>
        ),
      },
      {
        name: 'status',
        displayName: i18n.t('reservations.status'),
        component: (item: any) => (
          <ListStyled.StatusRow
            fontFamily={
              item.status === 'CONFIRMED'
                ? 'EncodeSans-SemiBold'
                : 'EncodeSans-Regular'
            }
            color={
              item.status === 'CONFIRMED' ? Colors.MediumBlue : Colors.Black
            }
          >
            {getDisplayNameStatusBooking(item.status)}
          </ListStyled.StatusRow>
        ),
      },
      {
        name: 'edit',
        displayName: i18n.t('reservations.edit'),
        component: (item: any) => (
          <ListStyled.ContainerEdit>
            <ListStyled.ContainerEditBooking
              onClick={() =>
                navigate(`/admin/handle-reservation-admin/${item.id}`)
              }
            >
              <SvgImage type={SvgType.Edit} />
            </ListStyled.ContainerEditBooking>
          </ListStyled.ContainerEdit>
        ),
      },
    ];
  }, []);

  const fieldsExpandedRows = useMemo(() => {
    return [
      {
        name: 'date',
        displayName: i18n.t('reservations.date'),
        component: (item: any) => {
          const date = getStringDateName(item.start);
          return (
            <TableCell key={'date'} sx={sxList.expandedRow} align="left">
              <ListStyled.ExpandedRow>{date}</ListStyled.ExpandedRow>
            </TableCell>
          );
        },
      },
      {
        name: 'time',
        displayName: i18n.t('reservations.time'),
        component: (item: any) => {
          const { time: timeStart } = getDateTimeBooking(item.start);
          const { time: timeEnd } = getDateTimeBooking(item.end);
          return (
            <TableCell key={'time'} sx={sxList.expandedRow} align="left">
              <ListStyled.ExpandedRow>
                {i18n.t('reservations.timeStartEnd', {
                  start: timeStart,
                  end: timeEnd,
                })}
              </ListStyled.ExpandedRow>
            </TableCell>
          );
        },
      },
      {
        name: 'room',
        displayName: i18n.t('reservations.room'),
        component: (item: any) => {
          const room = getRoom(rooms, item.product);
          return (
            <TableCell key={'room'} sx={sxList.expandedRow} align="left">
              <ListStyled.ExpandedRow>
                {room.name}
              </ListStyled.ExpandedRow>
            </TableCell>
          );
        },
      },
      {
        name: 'in_presence',
        displayName: i18n.t('reservations.inPresence'),
        component: (item: any) => {
          return (
            <TableCell key={'in_presence'} sx={sxList.expandedRow} align="left">
              <ListStyled.ExpandedRow>
                {item.attendees
                  ? `${item.attendees} invitat${item.attendees > 1 ? 'i' : 'o'}`
                  : '-'}
              </ListStyled.ExpandedRow>
            </TableCell>
          );
        },
      },
      {
        name: 'online',
        displayName: i18n.t('reservations.online'),
        component: (item: any) => {
          return (
            <TableCell key={'online'} sx={sxList.expandedRow} align="left">
              <ListStyled.ExpandedRow>
                {item.remote_attendees
                  ? `${item.remote_attendees} invitat${item.remote_attendees > 1 ? 'i' : 'o'}`
                  : '-'}
              </ListStyled.ExpandedRow>
              {item.zoomevent && (
                <ListStyled.CustomZoomDetail
                  target="_blank"
                  to={item.zoomevent.link}
                >
                  <ListStyled.CustomZoomDetailTitle>
                    {i18n.t('reservations.logEventZoom')}
                  </ListStyled.CustomZoomDetailTitle>
                </ListStyled.CustomZoomDetail>
              )}
            </TableCell>
          );
        },
      },
      {
        name: 'services',
        displayName: i18n.t('reservations.services'),
        component: (item: any) => {
          const servicesRoom = getRoom(rooms, item.product).services;
          const selectedServices = servicesRoom.filter((serviceRoom: any) =>
            item.services.some((service: string) => serviceRoom.id === service),
          );

          const showSelectedSmartObject =  selectedServices.some((service: any) => service.operator_choice_required === true)
          const selectedSmartObject = showSelectedSmartObject ? ` | ${i18n.t('reservations.tools')}: ` + getSmartObject(smartObjects, item.operator_management_choice) : ''

          return (
            <TableCell key={'services'} sx={sxList.servicesRow} align="left">
              <ListStyled.ExpandedRow>
                {selectedServices.length > 0
                  ? selectedServices
                      .map((service: any) => service.name)
                      .join(', ') + selectedSmartObject
                  : '-'}
              </ListStyled.ExpandedRow>
            </TableCell>
          );
        },
      },
    ];
  }, [reservationsList, rooms]);

  useEffect(() => {
    if (error) {
      openModal({
        message: error,
        variant: ModalVariant.Error,
        handleCloseModal: () => dispatch(getReservationListResetState()),
      });
    }
  }, [error]);

  useEffect(() => {
    if (updateDeleteSuccess) {
      dispatch(
        getReservationListAction({
          page: paginationInfo.pageCount,
          pageSize: paginationInfo.pageSize,
          statuses: statuses,
        }),
      );
    }
  }, [updateDeleteSuccess]);

  return (
    <Dashboard callingPage={'ReservationListAdminPage'}>
      <ReservationListAdminPageStyled.Container>
        <ReservationListAdminPageStyled.ListContainer>
          <List
            searchBarVisible
            containerComponent={ReservationListPageStyled.ListContainer}
            data={reservationsList}
            paginationInfo={paginationInfo}
            fields={fields}
            getDataAction={getReservationListAction}
            expandedRows={{
              fields: fieldsExpandedRows,
              selectedKeyRow: 'interval',
            }}
            loading={loading}
            statuses={statuses}
            expandedRowsColSpan={4}
          />
        </ReservationListAdminPageStyled.ListContainer>
      </ReservationListAdminPageStyled.Container>
    </Dashboard>
  );
};

export default ReservationListAdminPage;
